import clsx from 'clsx';
import React from 'react';
import { useField } from 'formik';

import OutlineBlack from './svg/powered_by_stripe_outline_black.svg?react';
import OutlineWhite from './svg/powered_by_stripe_outline_white.svg?react';

import usePage from '@dashboard/hooks/usePage';
import { Form } from '@dashboard/components/Formik';
import { checkoutLayouts } from '@repo/checkout/hooks/useCheckoutState';

const ShowHideElementsForm = () => {
  const { page, updatePage } = usePage();

  return (
    <Form
      autosave
      showState={false}
      initialValues={{
        showStoreNameWithLabel: page.showStoreNameWithLabel,
        showLogo: page.showLogo,
        showProductName: page.showProductName,
        showPrice: page.showPrice,
        showSetupFee: page.showSetupFee,
        showStoreName: page.showStoreName,
        showLocation: page.showLocation,
        showDate: page.showDate,
        showPaymentDetailsText: page.showPaymentDetailsText,
        showFormDetailsText: page.showFormDetailsText,
        showPriceBreakdown: page.showPriceBreakdown,
        showPayWhatYouWantPrice: page.showPayWhatYouWantPrice,
        showPriceOnButton: page.showPriceOnButton,
        showPoweredByStripe: page.showPoweredByStripe,
        poweredByStripeStyle: page.poweredByStripeStyle,
        showCheckoutPageBadge: page.showCheckoutPageBadge,
        showSecurePaymentsText: page.showSecurePaymentsText,
      }}
      onSubmit={async values => {
        const { data, error } = await updatePage.mutateAsync(values);

        return {
          data,
          error,
        };
      }}
      fields={({ values }) => [
        {
          type: 'fieldgroup',
          fields: [
            {
              type: 'checkbox',
              name: 'showStoreNameWithLabel',
              label: 'Logo with store name',
              hidden:
                page.usageType !== 'hosted' || page.hostedLayoutType === checkoutLayouts.CENTER,
            },
            {
              type: 'checkbox',
              name: 'showLogo',
              label:
                page.usageType === 'hosted' && page.hostedLayoutType === checkoutLayouts.SPLIT
                  ? 'Logo (on mobile)'
                  : 'Logo',
              hidden:
                page.usageType === 'hosted' && page.hostedLayoutType === checkoutLayouts.DOUBLE,
            },
            {
              type: 'checkbox',
              name: 'showProductName',
              ...(page.type === 'event' && {
                label: 'Event name',
              }),
              ...(page.type === 'checkout' && {
                label: 'Product title',
              }),
              ...(page.type === 'form' && {
                label: 'Title',
              }),
            },
            {
              type: 'checkbox',
              name: 'showPrice',
              label: 'Price',
              hidden: page.type !== 'checkout',
            },
            {
              type: 'checkbox',
              name: 'showSetupFee',
              label: 'Setup fee',
              hidden: page.type !== 'checkout',
            },
            {
              type: 'checkbox',
              name: 'showStoreName',
              label:
                page.usageType === 'hosted' && page.hostedLayoutType === checkoutLayouts.SPLIT
                  ? 'Store name (on mobile)'
                  : 'Store name',

              hidden:
                page.usageType === 'hosted' && page.hostedLayoutType === checkoutLayouts.DOUBLE,
            },
            {
              type: 'checkbox',
              name: 'showLocation',
              label: 'Location',
              hidden: page.type !== 'event',
            },
            {
              type: 'checkbox',
              name: 'showDate',
              label: 'Date',
              hidden: page.type !== 'event',
            },
            {
              type: 'checkbox',
              name: 'showPaymentDetailsText',
              label: 'Payment details',
              hidden:
                page.usageType !== 'hosted' ||
                page.type === 'form' ||
                page.hostedLayoutType === checkoutLayouts.CENTER ||
                page.hostedLayoutType === checkoutLayouts.SPLIT,
            },
            {
              type: 'checkbox',
              name: 'showFormDetailsText',
              label: 'Complete your details',
              hidden: page.type !== 'form' || page.hostedLayoutType === checkoutLayouts.CENTER,
            },
            {
              type: 'checkbox',
              name: 'showPriceBreakdown',
              label: 'Price breakdown',
              hidden: page.type === 'form',
            },
            {
              type: 'checkbox',
              name: 'showPayWhatYouWantPrice',
              label: 'Min. price on Pay what you want',
              hidden:
                page.type !== 'checkout' ||
                page.product?.type !== 'charge' ||
                !page.product?.payWhatYouWant,
            },
            {
              type: 'checkbox',
              name: 'showPriceOnButton',
              label: 'Price on button',
              hidden: page.type !== 'checkout' || page.product?.type !== 'charge',
            },
            {
              type: 'checkbox',
              name: 'showSecurePaymentsText',
              label: 'Secure payments text',
              hidden: page.type === 'form',
            },
            {
              type: 'checkbox',
              name: 'showPoweredByStripe',
              label: 'Powered by Stripe',
              hidden: page.type === 'form',
            },
            {
              type: 'custom',
              renderField: field => (
                <PoweredByStripeSelector
                  backgroundColor={
                    page.hostedLayoutType === checkoutLayouts.CENTER ? page.backgroundColor : '#fff'
                  }
                  {...field}
                />
              ),
              name: 'poweredByStripeStyle',
              label: 'Powered by Stripe style',
              hidden: page.type === 'form' || !values.showPoweredByStripe,
            },
          ],
        },
      ]}
    />
  );
};

const poweredByStripeStyles = [
  { value: 'outline_black', icon: <OutlineBlack />, label: 'Black badge' },
  { value: 'outline_white', icon: <OutlineWhite />, label: 'White badge' },
];

const PoweredByStripeSelector = ({ backgroundColor, ...props }) => {
  const [field, , helpers] = useField(props);
  const { setValue } = helpers;

  return (
    <div className="grid grid-cols-2 gap-4">
      {poweredByStripeStyles.map(style => (
        <div key={style.value}>
          <div
            className={clsx('flex rounded p-0.5 border', {
              'ring-1 ring-chocolate-600 border-chocolate-600': field.value === style.value,
            })}
          >
            <button
              type="button"
              onClick={() => setValue(style.value)}
              className={clsx('p-4 px-4 w-full flex justify-center items-center rounded ')}
              style={{ backgroundColor }}
            >
              {style.icon}
            </button>
          </div>

          <div className="text-center mt-2 font-medium text-sm text-gray-800">{style.label}</div>
        </div>
      ))}
    </div>
  );
};

export default ShowHideElementsForm;
